import type { ReactNode } from 'react';

import {
  documentToReactComponents,
  type Options,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import BpkBlockquote from '@skyscanner-internal/backpack-web/bpk-component-blockquote';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import {
  BpkList,
  BpkListItem,
} from '@skyscanner-internal/backpack-web/bpk-component-list';
import {
  BpkTable,
  BpkTableBody,
  BpkTableCell,
  BpkTableHeadCell,
  BpkTableRow,
} from '@skyscanner-internal/backpack-web/bpk-component-table';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import BlockQuote from './BlockQuote';
import {
  createMergedOptions,
  getRel,
  isExternal,
  resolveFullUrl,
} from './utils';

import type { Node } from '@contentful/rich-text-types';
import type {
  BlockQuoteProps,
  RichTextProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/RichTextProps';

import STYLES from './RichText.module.scss';

type DefaultOptionsFunction = (linkClickHandler?: Function) => Options;

const getDefaultOptions: DefaultOptionsFunction = (linkClickHandler) => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Node, children: ReactNode) => (
      <h1 className={classNames(STYLES.Heading, STYLES.Heading1)}>
        {children}
      </h1>
    ),
    [BLOCKS.HEADING_2]: (node: Node, children: ReactNode) => (
      <h2 className={classNames(STYLES.Heading, STYLES.Heading2)}>
        {children}
      </h2>
    ),
    [BLOCKS.HEADING_3]: (node: Node, children: ReactNode) => (
      <h3 className={classNames(STYLES.Heading, STYLES.Heading3)}>
        {children}
      </h3>
    ),
    [BLOCKS.HEADING_4]: (node: Node, children: ReactNode) => (
      <h4 className={classNames(STYLES.Heading, STYLES.Heading4)}>
        {children}
      </h4>
    ),
    [BLOCKS.HEADING_5]: (node: Node, children: ReactNode) => (
      <h5 className={classNames(STYLES.Heading, STYLES.Heading5)}>
        {children}
      </h5>
    ),
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => (
      <p className={STYLES.Paragraph}>{children}</p>
    ),
    [BLOCKS.UL_LIST]: (node: Node, children: ReactNode) => (
      <BpkList>{children}</BpkList>
    ),
    [BLOCKS.OL_LIST]: (node: Node, children: ReactNode) => (
      <BpkList ordered>{children}</BpkList>
    ),
    [BLOCKS.LIST_ITEM]: (node: Node, children: ReactNode) => (
      <BpkListItem>{children}</BpkListItem>
    ),
    [BLOCKS.QUOTE]: (node: Node, children: ReactNode) => (
      <BpkBlockquote>{children}</BpkBlockquote>
    ),
    [BLOCKS.TABLE]: (node: Node, children: ReactNode) => (
      <BpkTable>
        <BpkTableBody>{children}</BpkTableBody>
      </BpkTable>
    ),
    [BLOCKS.TABLE_ROW]: (node: Node, children: ReactNode) => (
      <BpkTableRow>{children}</BpkTableRow>
    ),
    [BLOCKS.TABLE_CELL]: (node: Node, children: ReactNode) => (
      <BpkTableCell wordBreak>{children}</BpkTableCell>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node: Node, children: ReactNode) => (
      <BpkTableHeadCell wordBreak>{children}</BpkTableHeadCell>
    ),
    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
      try {
        const url = resolveFullUrl(node.data.uri);
        const blank = isExternal(url);
        const rel = getRel(url);

        return (
          <BpkLink
            href={url.href}
            blank={blank}
            rel={rel}
            onClick={linkClickHandler}
          >
            {children}
          </BpkLink>
        );
      } catch (e) {
        return (
          <BpkLink href={node.data.uri} onClick={linkClickHandler}>
            {children}
          </BpkLink>
        );
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node, children: ReactNode) => {
      if (node.nodeType !== 'embedded-entry-block') return children;

      const { anchorId, contentTypeId } = node.data ?? {};

      // If it's an anchor marker, render the anchor span
      if (
        contentTypeId === 'RichTextWithAnchor' &&
        typeof anchorId === 'string' &&
        anchorId
      ) {
        return <span id={anchorId} tabIndex={-1} aria-label={anchorId} />;
      }

      // Otherwise, render children as usual
      return children;
    },
  },
  renderText: (text: string): React.ReactNode =>
    text.split('\n').reduce<React.ReactNode[]>((acc, segment, index) => {
      if (index > 0) acc.push(<br />);
      acc.push(segment);
      return acc;
    }, []),
});

const larkenOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => (
      <BpkText tagName="p" textStyle={TEXT_STYLES.editorial2}>
        {children}
      </BpkText>
    ),
  },
};

const RichText = (props: RichTextProps & BlockQuoteProps) => {
  if (props?.quote && props?.variant) {
    const { isConstrained, linkClickHandler, quote, topTipTitle, variant } =
      props;

    const options = createMergedOptions(
      getDefaultOptions(linkClickHandler),
      larkenOptions,
    );
    const reactComponents = documentToReactComponents(quote, options);
    return (
      <BlockQuote
        isConstrained={isConstrained}
        topTipTitle={topTipTitle}
        variant={variant}
      >
        {reactComponents}
      </BlockQuote>
    );
  }

  const {
    appendComponent,
    body,
    linkClickHandler,
    options: customOptions,
  } = props;

  let options = getDefaultOptions(linkClickHandler);

  if (customOptions) {
    options = createMergedOptions(
      getDefaultOptions(linkClickHandler),
      customOptions,
    );
  }

  const reactComponents = documentToReactComponents(body, options);

  return (
    <div className={STYLES.RichTextBlock}>
      {reactComponents}
      {appendComponent}
    </div>
  );
};

export default RichText;
